import React, { useState } from 'react';
import './PendingPlanPreview.css';

/**
 * The anonymous build, readable in the chat before signup (chat plan preview,
 * decisions 1, 3, 5, 9, 10). It renders the stored `pending_plan` exactly as
 * the build prompt shaped it — `title`, `destination`, `days[{day, date,
 * theme, activities[{time, name, description}]}]` — and nothing else.
 *
 * The plan comes from localStorage, so nothing about its shape is trusted:
 * every field is guarded, whatever is there renders, and a value of the wrong
 * type is skipped rather than thrown on. Read-only: no action lives here; the
 * save bar below it is the one action.
 */

type Rec = Record<string, unknown>;

const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * `YYYY-MM-DD` as a local-noon `Date`, or `null` when it is not a real
 * calendar date. A date-time string with no offset is parsed as LOCAL time;
 * the date-only form would be parsed as UTC and show the previous day west of
 * Greenwich. V8 rolls an impossible day over (`2026-02-30` becomes Mar 2), so
 * the parsed date must read back as the same year, month and day.
 */
const parsePlanDate = (date: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (
    parsed.getFullYear() !== Number(m[1])
    || parsed.getMonth() + 1 !== Number(m[2])
    || parsed.getDate() !== Number(m[3])
  ) return null;
  return parsed;
};

/** `YYYY-MM-DD` as `Wed, Oct 7`; anything that is not a real date verbatim. */
export const formatPlanDay = (date: string): string => {
  const parsed = parsePlanDate(date);
  return parsed ? parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : date;
};

/** `HH:MM` (24-hour) as `2:30 PM`; anything else verbatim. */
export const formatPlanTime = (time: string): string => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return time;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return time;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m[2]} ${hour >= 12 ? 'PM' : 'AM'}`;
};

/** Short month-day for the range line (`Oct 7`); anything that is not a real
 *  date verbatim, as the day header renders it (and as mobile's range does). */
const formatRangeEnd = (date: string): string | null => {
  if (!date) return null;
  const parsed = parsePlanDate(date);
  return parsed ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : date;
};

interface Activity { key: string; time: string; name: string; description: string }
interface Day { key: string; label: string; date: string; theme: string; activities: Activity[] }

const readDays = (raw: unknown): Day[] => {
  if (!Array.isArray(raw)) return [];
  const days: Day[] = [];
  raw.forEach((d, i) => {
    if (!isRec(d)) return;
    const n = typeof d.day === 'number' && Number.isFinite(d.day) ? d.day : days.length + 1;
    const acts: Activity[] = [];
    if (Array.isArray(d.activities)) {
      d.activities.forEach((a, j) => {
        if (!isRec(a)) return;
        const name = text(a.name);
        const time = text(a.time);
        const description = text(a.description);
        if (!name && !description) return;
        acts.push({ key: `a-${j}`, time, name, description });
      });
    }
    days.push({ key: `d-${i}`, label: `Day ${n}`, date: text(d.date), theme: text(d.theme), activities: acts });
  });
  return days;
};

const PendingPlanPreview: React.FC<{ plan: unknown }> = ({ plan }) => {
  const [expanded, setExpanded] = useState(false);
  if (!isRec(plan)) return null;

  const title = text(plan.title);
  const destination = text(plan.destination);
  const days = readDays(plan.days);
  if (!title && !destination && days.length === 0) return null;

  const first = days.length ? formatRangeEnd(days[0].date) : null;
  const last = days.length ? formatRangeEnd(days[days.length - 1].date) : null;
  const range = first && last ? (first === last ? first : `${first} – ${last}`) : null;
  const dayCount = days.length ? `${days.length} ${days.length === 1 ? 'day' : 'days'}` : null;

  return (
    <section className="pending-plan" data-testid="pending-plan-preview" aria-label="Your plan, not saved yet">
      {title && <h3 className="pending-plan-title">{title}</h3>}
      {destination && <p className="pending-plan-destination">{destination}</p>}
      {(range || dayCount) && (
        <p className="pending-plan-meta">
          {range && <span className="pending-plan-mono">{range}</span>}
          {range && dayCount && ' · '}
          {dayCount && <span className="pending-plan-mono">{dayCount}</span>}
        </p>
      )}
      {days.length > 0 && (
        <ol className="pending-plan-days">
          {days.map((day, i) => {
            const open = i === 0 || expanded;
            return (
              <li key={day.key} className="pending-plan-day">
                <p className="pending-plan-day-header">
                  <span>{day.label}</span>
                  {day.date && (
                    <>
                      {' · '}
                      <span className="pending-plan-mono">{formatPlanDay(day.date)}</span>
                    </>
                  )}
                </p>
                {day.theme && <p className="pending-plan-theme">{day.theme}</p>}
                {open && day.activities.length > 0 && (
                  <ul className="pending-plan-activities">
                    {day.activities.map((a) => (
                      <li key={a.key} className="pending-plan-activity">
                        <p className="pending-plan-activity-line">
                          {a.time && <span className="pending-plan-mono">{formatPlanTime(a.time)}</span>}
                          {a.time && a.name && ' · '}
                          {a.name && <span className="pending-plan-activity-name">{a.name}</span>}
                        </p>
                        {a.description && <p className="pending-plan-activity-desc">{a.description}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {days.length > 1 && (
        <button
          type="button"
          className="pending-plan-toggle"
          data-testid="pending-plan-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show day 1 only' : `Show all ${days.length} days`}
        </button>
      )}
    </section>
  );
};

export default PendingPlanPreview;
